import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    LogOut, LayoutDashboard, PanelLeftClose, PanelLeftOpen, MoreHorizontal, X,
    CircleHelp, UtensilsCrossed, Package, ChefHat, Truck, FileText, BookOpen
} from 'lucide-react';
import mascot from '../assets/calum_mascot.png';

const NAV = [
    { path: '/dashboard', label: 'Tableau de bord', short: 'Accueil', icon: LayoutDashboard, tab: true },
    { path: '/dishes', label: 'Plats', icon: UtensilsCrossed, tab: true },
    { path: '/ingredients', label: 'Ingrédients', icon: Package, tab: true },
    { path: '/questions', label: 'Questions', icon: CircleHelp, tab: true },
    { path: '/preparations', label: 'Préparations', icon: ChefHat },
    { path: '/invoices', label: 'Factures', icon: FileText },
    { path: '/suppliers', label: 'Fournisseurs', icon: Truck },
    { path: '/guide', label: 'Guide', icon: BookOpen },
];

/**
 * App shell: a green rail on larger screens, a bottom tab bar on phones.
 */
const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);

    // The app has a single light theme; clear any preference left by older versions.
    useEffect(() => {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
    }, []);

    const isActive = (path) => location.pathname === path;
    const more = NAV.filter((item) => !item.tab);

    return (
        <div className="min-h-screen md:flex">
            <aside className={`rail ${collapsed ? 'is-collapsed' : ''}`}>
                <div className="space-y-6">
                    <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between px-1'}`}>
                        {!collapsed && (
                            <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-2.5">
                                <img src={mascot} alt="" className="mascot h-10 w-10" />
                                <span className="font-display text-2xl">Costy</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
                            className="p-2 rounded-full hover:bg-accent"
                        >
                            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                        </button>
                    </div>
                    <nav className="space-y-1">
                        {NAV.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.path}
                                    type="button"
                                    title={item.label}
                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                    onClick={() => navigate(item.path)}
                                    className={`rail-link ${isActive(item.path) ? 'is-active' : ''} ${collapsed ? 'justify-center' : ''}`}
                                >
                                    <Icon className="h-4 w-4 shrink-0" />
                                    {!collapsed && item.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>
                <button type="button" onClick={logout} title={user?.username ? `Se déconnecter (${user.username})` : 'Se déconnecter'} className={`rail-link ${collapsed ? 'justify-center' : ''}`}>
                    <LogOut className="h-4 w-4 shrink-0" />
                    {!collapsed && 'Se déconnecter'}
                </button>
            </aside>

            <main className="app-main">{children}</main>

            <nav className="tabbar" aria-label="Navigation">
                {NAV.filter((item) => item.tab).map((item) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.path}
                            type="button"
                            aria-current={isActive(item.path) ? 'page' : undefined}
                            onClick={() => navigate(item.path)}
                            className={`tab ${isActive(item.path) ? 'is-active' : ''}`}
                        >
                            <span className="tab-icon"><Icon className="h-5 w-5" /></span>
                            {item.short || item.label}
                        </button>
                    );
                })}
                <button type="button" onClick={() => setMoreOpen(true)} className={`tab ${more.some((item) => isActive(item.path)) ? 'is-active' : ''}`}>
                    <span className="tab-icon"><MoreHorizontal className="h-5 w-5" /></span>
                    Plus
                </button>
            </nav>

            {moreOpen && (
                <div className="sheet-overlay md:hidden" onClick={() => setMoreOpen(false)}>
                    <div className="sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-xl">Plus</h2>
                            <button type="button" onClick={() => setMoreOpen(false)} aria-label="Fermer" className="p-2 rounded-full hover:bg-accent">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        {more.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.path}
                                    type="button"
                                    onClick={() => { setMoreOpen(false); navigate(item.path); }}
                                    className="flex w-full items-center gap-3 min-h-[52px] border-t border-border text-left font-semibold"
                                >
                                    <Icon className="h-5 w-5" />
                                    {item.label}
                                </button>
                            );
                        })}
                        <button type="button" onClick={logout} className="flex w-full items-center gap-3 min-h-[52px] border-t border-border text-left font-semibold text-bad">
                            <LogOut className="h-5 w-5" />
                            Se déconnecter
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layout;
